import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ContentPage, ContentSection } from '@/components/marketing/content-page';
import { ContentFaq } from '@/components/marketing/content-faq';
import {
  setDocumentMeta, setCanonical, injectJsonLdList,
  buildWebPageSchema, buildFaqPageSchema, buildItemListSchema, buildBreadcrumbSchema,
} from '@/lib/seo';

const FAQS = [
  {
    q: 'What is a ticketing system?',
    a: 'A ticketing system is software that turns incoming requests — customer emails, chat messages, bug reports, IT incidents — into trackable tickets, each with a title, category, priority, owner, and status. Instead of requests living in inboxes and chat threads, every ticket moves through a visible lifecycle from open to resolved, so nothing is lost and response times can be measured.',
  },
  {
    q: 'How does a ticketing system work?',
    a: 'A ticketing system works in four steps: intake (a request arrives from email, chat, a web form, or an API call), creation (the system records it as a ticket with a title, category, and priority — AI-native systems like Kora generate these automatically from the raw message), routing (the ticket is assigned to the right person or team, often with SLA timers), and resolution (the agent replies, updates the status, and the ticket closes with a full history retained for reporting).',
  },
  {
    q: 'What is a ticketing system in customer service?',
    a: 'In customer service, a ticketing system is the shared workspace where every customer request becomes a trackable ticket. It replaces shared inboxes: instead of replying from a group email with no visibility, agents see queue, priority, and status for every request; managers see response and resolution metrics; and customers get consistent answers because history is attached to each ticket.',
  },
  {
    q: 'What is the best ticketing system for small business?',
    a: 'For small businesses the best ticketing systems in 2026 are the ones with a usable free start and predictable growth costs: Kora (pay-as-you-go credits ($1 = 10 credits, 1 credit per ticket, 1,100 free on signup, unlimited members) with free AI ticket structuring), Freshdesk (from $19/agent/month), and Zoho Desk (free for 3 users). The deciding factors are usually who pays per seat, whether AI costs extra, and how messy your input channels are.',
  },
];

const SYSTEM_EXAMPLES = [
  { name: 'Kora', category: 'AI-native help desk', detail: 'Pay-as-you-go credits — $1 = 10 credits, 1 credit per ticket; AI structures every ticket.', path: '/' },
  { name: 'Zendesk', category: 'Customer support', detail: 'Suite Team $55/agent/mo; the enterprise standard.', path: '/compare/kora-vs-zendesk' },
  { name: 'Freshdesk', category: 'Customer support', detail: 'Growth $19/agent/mo on annual billing.', path: '/compare/kora-vs-freshdesk' },
  { name: 'Help Scout', category: 'Customer support', detail: 'Email-first shared inbox; free plan for up to 5 users and 100 contacts.', path: '/compare/kora-vs-help-scout' },
  { name: 'Jira Service Management', category: 'IT service desk', detail: 'Atlassian ITSM; popular with engineering orgs.', path: '/compare/kora-vs-jira-service-management' },
  { name: 'ServiceNow', category: 'IT service desk', detail: 'Enterprise ITSM with CMDB and workflows.', path: '/compare/kora-vs-servicenow' },
  { name: 'Jira', category: 'Issue tracker', detail: 'The default agile tracker for engineering teams.', path: '/compare/kora-vs-jira' },
  { name: 'Linear', category: 'Issue tracker', detail: 'Fast, opinionated tracking for product teams.', path: '/compare/kora-vs-linear' },
];

const TICKET_EXAMPLES = [
  {
    type: 'Bug report',
    raw: '\u201CThe checkout page crashes when I pay with a card. Happens on iPhone. Order #1046.\u201D',
    structured: {
      title: 'Checkout page crashing on card payments',
      category: 'Product Bug',
      priority: 'Urgent',
      note: 'AI extracts title, category, and priority from the raw message in seconds.',
    },
  },
  {
    type: 'Feature request',
    raw: '\u201CCan you add dark mode to the dashboard? My eyes at 2am would thank you.\u201D',
    structured: {
      title: 'Add dark mode to the dashboard',
      category: 'Feature Request',
      priority: 'Low',
      note: 'Requests get logged with context instead of being lost in chat.',
    },
  },
  {
    type: 'Access request (IT)',
    raw: '\u201CNew hire starting Monday — needs laptop admin rights and VPN access please.\u201D',
    structured: {
      title: 'Provision laptop admin rights and VPN for new hire',
      category: 'IT · Access Request',
      priority: 'Medium',
      note: 'Internal IT requests follow the same lifecycle as customer tickets.',
    },
  },
  {
    type: 'Billing question',
    raw: '\u201CHi, I was charged twice this month? Can someone check my invoice?\u201D',
    structured: {
      title: 'Customer charged twice — invoice review needed',
      category: 'Billing',
      priority: 'High',
      note: 'Billing tickets route to finance with the customer record attached.',
    },
  },
];

export function TicketingSystemExamplesPage() {
  useEffect(() => {
    const title = 'Ticketing System Examples — Systems and Real Tickets (2026)';
    const description =
      'Ticketing system examples by category — customer support, IT, issue tracking — plus four real examples of support tickets before and after structuring.';
    setDocumentMeta(title, description);
    setCanonical('/ticketing-system-examples');

    const cleanup = injectJsonLdList([
      buildWebPageSchema('/ticketing-system-examples', title, description),
      buildBreadcrumbSchema([
        { name: 'Home', url: 'https://usekora.app' },
        { name: 'Ticketing System Examples', url: 'https://usekora.app/ticketing-system-examples' },
      ]),
      buildItemListSchema(
        SYSTEM_EXAMPLES.map((s) => ({ name: s.name, url: `https://usekora.app${s.path}` })),
      ),
      buildFaqPageSchema(FAQS),
    ]);
    return cleanup;
  }, []);

  return (
    <ContentPage
      pill="Guide · Updated September 2026"
      title="Ticketing system examples"
      subtitle="The main ticketing systems by category, plus what real tickets look like before and after structuring."
      answer={
        <p>
          Ticketing system examples fall into three main categories. Customer support systems
          include <strong>Zendesk</strong> (Suite Team from $55/agent/month),{' '}
          <strong>Freshdesk</strong> (from $19/agent/month), <strong>Help Scout</strong>, and{' '}
          <strong>Kora</strong>. IT service desks include <strong>Jira Service Management</strong>{' '}
          and <strong>ServiceNow</strong>. Issue trackers include <strong>Jira</strong>,{' '}
          <strong>Linear</strong>, and <strong>GitHub Issues</strong>. A ticketing system works by
          turning each request into a ticket with a title, category, priority, owner, and status —
          AI-native systems like Kora generate these fields automatically from the raw message.
          Typical ticket examples include bug reports, feature requests, access requests, and
          billing questions.
        </p>
      }
    >
      <ContentSection title="Ticketing systems by category">
        <div className="rounded-xl border border-zinc-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-zinc-200 bg-zinc-50 text-left">
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-zinc-500">System</th>
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-zinc-500">Category</th>
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-zinc-500">Notes</th>
              </tr>
            </thead>
            <tbody>
              {SYSTEM_EXAMPLES.map((s) => (
                <tr key={s.name} className="border-b border-zinc-100 last:border-0">
                  <td className="px-4 py-3 font-medium text-zinc-900 ">
                    <Link to={s.path} className="hover:underline">{s.name}</Link>
                  </td>
                  <td className="px-4 py-3 text-zinc-500 ">{s.category}</td>
                  <td className="px-4 py-3 text-zinc-500 ">{s.detail}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </ContentSection>

      <ContentSection title="Real ticket examples — before and after">
        <p>
          What actually lands in a ticketing system is messy. Below are four real-world ticket
          examples: the raw message a team receives, and the structured ticket that comes out.
        </p>
        <div className="space-y-4 not-prose">
          {TICKET_EXAMPLES.map((t) => (
            <div key={t.type} className="rounded-xl border border-zinc-200 overflow-hidden">
              <div className="px-4 py-3 bg-zinc-50 border-b border-zinc-200 ">
                <span className="text-xs font-semibold uppercase tracking-wide text-zinc-500">{t.type}</span>
              </div>
              <div className="p-4 space-y-3">
                <div>
                  <div className="text-[11px] font-medium uppercase tracking-wide text-zinc-400 mb-1">What the customer sends</div>
                  <p className="text-sm text-zinc-600 italic">{t.raw}</p>
                </div>
                <div>
                  <div className="text-[11px] font-medium uppercase tracking-wide text-zinc-400 mb-1">Structured ticket</div>
                  <div className="flex flex-wrap gap-2 text-xs">
                    <span className="rounded-md bg-zinc-100 px-2 py-1 font-medium">{t.structured.title}</span>
                    <span className="rounded-md bg-blue-50 text-blue-700 px-2 py-1 font-medium">{t.structured.category}</span>
                    <span className="rounded-md bg-amber-50 text-amber-700 px-2 py-1 font-medium">{t.structured.priority}</span>
                  </div>
                </div>
                <p className="text-xs text-zinc-400 ">{t.structured.note}</p>
              </div>
            </div>
          ))}
        </div>
      </ContentSection>

      <ContentSection title="How a ticketing system works">
        <p>
          Every ticketing system, regardless of category, runs the same lifecycle:{' '}
          <strong>intake</strong> (email, chat, web form, or API), <strong>creation</strong>{' '}
          (title, category, priority — manual in legacy systems, automatic in AI-native ones),{' '}
          <strong>routing</strong> (assignment to a person or team, optionally with SLA timers),
          and <strong>resolution</strong> (reply, status change, close — with full history
          retained for reporting).
        </p>
        <p>
          The systems differ in what they assume at intake. Traditional helpdesks assume clean,
          categorized input and charge per agent. AI-native systems accept messy input — Slack
          threads, WhatsApp forwards, pasted emails — and do the structuring themselves.
        </p>
      </ContentSection>

      <ContentFaq faqs={FAQS} subtitle="Questions people ask about ticketing systems." />
    </ContentPage>
  );
}
